import { css } from '@emotion/react';
import { useParentValueOnceSettled } from '@kaiizen/kai-pro-api-react-state';
import { useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { CountInputSize, CountInputStyles, DEFAULT_COUNT_INPUT_STYLES } from './countInputStyles';

const MINUS_BUTTON_STYLE = css`
  label: MINUS_BUTTON_STYLE;
`;

const PLUS_BUTTON_STYLE = css`
  label: PLUS_BUTTON_STYLE;
`;

export const CountInput = ({
  value,
  onUpdate,
  min = 0,
  max,
  width = '100%',
  step = 1,
  placeholder = '0',
  disabled = false,
  delay = 1000,
  size = 'small',
  styles = DEFAULT_COUNT_INPUT_STYLES,
}: {
  value: number | undefined;
  onUpdate: (value: number) => void;
  min?: number;
  max?: number;
  width?: string;
  step?: number;
  placeholder?: string;
  disabled?: boolean;
  delay?: number;
  size?: CountInputSize;
  styles?: CountInputStyles;
}) => {
  const [inputValue, setInputValue] = useState(value?.toString() ?? '-');
  // Mirrors inputValue so rapid taps handled before a re-render still count
  // from the latest one (KAIDEV-4328).
  const inputValueRef = useRef(inputValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const showValue = (newValue: string) => {
    inputValueRef.current = newValue;
    setInputValue(newValue);
  };

  const sendUpdate = (newValue: number) => {
    markEdited();
    onUpdate(newValue);
  };

  const debouncedUpdate = useDebouncedCallback(
    (newValue: string) => {
      if (newValue === '-') {
        // Don't call onUpdate for "-" - keep value as undefined
        return;
      }
      const numericValue = parseFloat(newValue);
      if (!isNaN(numericValue)) {
        const clampedValue = Math.max(min, max != null ? Math.min(max, numericValue) : numericValue);
        sendUpdate(clampedValue);
      } else if (newValue === '') {
        sendUpdate(min);
      }
    },
    delay,
  );

  // Show the prop value - except while taps or typing are still coming in: the
  // parent passes back earlier saves after later edits, and showing one would
  // drop the newer edits still on screen (KAIDEV-4328).
  const { markEdited } = useParentValueOnceSettled(
    value,
    parentValue => showValue(parentValue?.toString() ?? '-'),
    () => debouncedUpdate.isPending(),
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    showValue(newValue);
    debouncedUpdate(newValue);
  };

  const handleInputFocus = () => {
    inputRef.current?.select();
  };

  const handleInputBlur = () => {
    // Immediately process the value on blur
    debouncedUpdate.flush();

    if (inputValue === '-') {
      // Keep "-" as display but don't change the undefined value
      return;
    }

    const numericValue = parseFloat(inputValue);
    if (!isNaN(numericValue)) {
      const clampedValue = Math.max(min, max != null ? Math.min(max, numericValue) : numericValue);
      sendUpdate(clampedValue);
      showValue(clampedValue.toString());
    } else if (inputValue === '') {
      sendUpdate(min);
      showValue(min.toString());
    } else {
      // Reset to current value if input is invalid
      showValue(value?.toString() ?? '-');
    }
  };

  // Count from what's on screen, not the `value` prop: the prop only moves once
  // the parent re-renders with the saved result, so rapid taps counted from it
  // would all send the same number (KAIDEV-4328).
  const getCurrentValue = (displayed: string) => {
    const displayedValue = parseFloat(displayed);
    return isNaN(displayedValue) ? value ?? min : displayedValue;
  };
  const currentValue = getCurrentValue(inputValue);

  // A tap is a decision: show it and save it at once, cancelling any typing
  // still waiting to save.
  const updateFromButton = (newValue: number) => {
    debouncedUpdate.cancel();
    showValue(newValue.toString());
    sendUpdate(newValue);
  };

  const handleDecrement = () => {
    if (disabled) return;
    updateFromButton(Math.max(min, getCurrentValue(inputValueRef.current) - step));
  };

  const handleIncrement = () => {
    if (disabled) return;
    const latestValue = getCurrentValue(inputValueRef.current);
    updateFromButton(max != null ? Math.min(max, latestValue + step) : latestValue + step);
  };

  const canDecrement = !disabled && currentValue > min;
  const canIncrement = !disabled && (max === undefined || currentValue < max);

  return (
    <div css={styles.container} style={{ width }}>
      {!disabled
        ? (
          <button
            css={[styles.button, MINUS_BUTTON_STYLE]}
            onClick={handleDecrement}
            disabled={!canDecrement}
            type='button'
          >
            −
          </button>
        )
        : null}
      <input
        ref={inputRef}
        css={[styles.input, styles.inputSizes?.[size], disabled && styles.inputDisabled]}
        type='number'
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        placeholder={placeholder}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
      />
      {!disabled
        ? (
          <button
            css={[styles.button, PLUS_BUTTON_STYLE]}
            onClick={handleIncrement}
            disabled={!canIncrement}
            type='button'
          >
            +
          </button>
        )
        : null}
    </div>
  );
};
